import {
  createRouter as _createRouter,
  createWebHistory,
  isNavigationFailure,
  NavigationFailureType,
} from 'vue-router';
import type { Router, RouteLocation } from 'vue-router';
import { ModuleNames } from '@/app/shared/module-names';
import { routes } from './routes';
import { runGuards, type Guard } from './guards';

declare module 'vue-router' {
  interface RouteMeta {
    guards?: Guard[];
    trackVisit?: boolean;
    title?:
      | ((route: RouteLocation) => string | null)
      | ((route: RouteLocation) => Promise<string | null>)
      | string
      | null;
    moduleName?: ModuleNames;
    layoutFeatures?: { sidebar?: boolean };
  }
}

export function createRouter(): Router {
  const router = _createRouter({
    routes,
    history: createWebHistory('/'),
    linkActiveClass: '',
    linkExactActiveClass: '',
    async scrollBehavior(to, from, savedScrollPosition) {
      // TODO: find best approach for delaying scroll behavior
      // https://router.vuejs.org/guide/advanced/scroll-behavior.html#delaying-the-scroll
      if (savedScrollPosition) {
        return savedScrollPosition;
      }

      // Change route query without scroll jumping
      if (from.path === to.path) {
        return undefined;
      }

      if (to.hash) {
        return { el: to.hash };
      }

      return { left: 0, top: 0 };
    },
  });

  router.onError((error, to) => {
    if (!__DEV__ && error.message.includes('Failed to fetch dynamically imported module')) {
      window.location.href = to.fullPath;
    }
  });

  let redirectsCount = 0;
  let lastNavigationId;

  router.beforeEach(async (to, from) => {
    const navigationId = Date.now();
    lastNavigationId = navigationId;

    const result = (await runGuards(from, to)) ?? true;

    // If navigation was cancelled, but guards return redirect - redirect will be done.
    // Prevent this behavior.
    if (lastNavigationId !== navigationId) {
      return;
    }

    if (typeof result !== 'boolean') {
      redirectsCount++;
    }

    if (redirectsCount >= 10) {
      redirectsCount = 0;
      throw new Error('Redirect loop detected');
    }

    return result;
  });

  router.afterEach((to, from, failure) => {
    if (isNavigationFailure(failure, NavigationFailureType.cancelled)) {
      return;
    }

    redirectsCount = 0;
  });

  return router;
}

export * from './guards';
